import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Factory } from "lucide-react";
import { Reveal, SplitLines } from "@/components/Reveal";
import { REGIONS, BRAND } from "@/lib/site";

const Arcs = () => (
  <svg viewBox="0 0 600 600" className="absolute -right-24 -bottom-32 h-[640px] w-[640px] opacity-40" aria-hidden>
    {[...Array(9)].map((_, i) => (
      <motion.circle key={i} cx="300" cy="300" r={60 + i * 30} fill="none" stroke="url(#g)" strokeWidth="1.2" strokeDasharray="4 10"
        initial={{ rotate: 0 }} animate={{ rotate: i % 2 ? -360 : 360 }} transition={{ duration: 80 + i * 10, repeat: Infinity, ease: "linear" }} style={{ originX: "300px", originY: "300px" }} />
    ))}
    <defs><linearGradient id="g" x1="0" x2="1"><stop offset="0" stopColor="#2f6f8f" /><stop offset="1" stopColor="#71c3a2" /></linearGradient></defs>
  </svg>
);

export const Global = () => {
  const [hover, setHover] = useState(0);
  return (
    <section id="global" data-testid="global-section" className="relative overflow-hidden bg-[var(--navy)] py-28 text-white lg:py-40">
      <Arcs />
      <div className="container-x relative grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal><p className="eyebrow eyebrow-dark mb-6">Across the Globe</p></Reveal>
          <SplitLines as="h2" inView className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.05] tracking-tight" lines={["Crafted in India.", <>Designed for <em className="italic text-[var(--mint-light)]">the world.</em></>]} />
          <Reveal delay={0.3}><p className="mt-8 max-w-md text-base leading-relaxed text-slate-300">Our export desk ships full and mixed containers to seven regions, with region-specific certification, packing and shade-matched reorders handled end to end.</p></Reveal>

          <div className="mt-12 grid gap-4 sm:grid-cols-2">
            {[{ icon: MapPin, ...BRAND.headOffice }, { icon: Factory, ...BRAND.factory }].map((l) => (
              <Reveal key={l.label} delay={0.4} className="glass-dark rounded-2xl p-5">
                <l.icon size={18} className="text-[var(--mint-light)]" />
                <div className="mt-4 font-mono text-[0.58rem] uppercase tracking-[0.25em] text-slate-400">{l.label}</div>
                <div className="mt-1 font-display text-xl">{l.city}</div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-6 lg:col-start-7" data-testid="regions-list">
          {REGIONS.map((r, i) => (
            <motion.div
              key={r.code}
              data-testid={`region-${r.code}`}
              onMouseEnter={() => setHover(i)}
              initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.07 }}
              className={`group flex items-center justify-between gap-6 border-t border-white/10 py-6 transition-colors duration-500 ${hover === i ? "text-white" : "text-slate-400"}`}
            >
              <div className="flex items-baseline gap-6">
                <span className="font-mono text-xs text-[var(--mint-light)]">0{i + 1}</span>
                <h3 className="font-display text-2xl font-light tracking-tight sm:text-4xl">{r.name}</h3>
              </div>
              <span className={`hidden max-w-[220px] text-right text-xs leading-relaxed transition-opacity duration-500 sm:block ${hover === i ? "opacity-100" : "opacity-40"}`}>{r.markets}</span>
            </motion.div>
          ))}
          <div className="border-t border-white/10" />
        </div>
      </div>
    </section>
  );
};
